import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { MenuTooltipTool } from '../../../../../../viewer/tools/tooltip-helper';
import { CoordinatePropertiesStyled } from '../../../tooltip/components/coordinate-properties';
import {
  AssetPositionProperties,
  hasTooltipContent,
  SkyviewAssetPositionProps,
} from '../../../tooltip/skyview-asset-position-tooltip';

type Props = {
  tool: MenuTooltipTool;
  /**
   * Shows the tooltip of the tool again, so that it moves between the cursor and the menu as soon
   * as it's placed there or removed. Otherwise it moves as the cursor is moved.
   */
  refresh?: () => void;
  /** Above what it shows. */
  title?: string;
  /**
   * Styles the band, e.g. its height in the menu or the dialog.
   */
  className?: string;
  /** Kept mounted but hidden, e.g. while the dialog shows the help of a control instead. */
  hidden?: boolean;
};

/**
 * The tooltip of a tool, in a band across the bottom of a menu or a dialog instead of following
 * the cursor, e.g. of the design tool in the right-side menu.
 *
 * It is as high as what it shows, and below what it is in, so that what is above it doesn't move.
 * When the cursor leaves the view, it keeps what it showed last, faded, so that it doesn't shrink
 * as the cursor reaches it: the cursor would be back in the view, and it would grow again, and
 * flicker.
 */
export const MenuTooltip = (props: Props) => {
  const { t } = useTranslation();
  const [shown, setShown] = React.useState<{
    tooltip?: SkyviewAssetPositionProps;
    /** Kept from before, the cursor having left the view since. */
    stale: boolean;
  }>({ stale: false });
  const refresh = React.useRef(props.refresh);
  refresh.current = props.refresh;

  React.useEffect(() => {
    props.tool.onMenuTooltipChanged = (tooltip) =>
      setShown((current) =>
        tooltip && hasTooltipContent(tooltip)
          ? { tooltip, stale: false }
          : current.tooltip
            ? { tooltip: current.tooltip, stale: true }
            : { stale: false },
      );
    // Moves a tooltip shown next to the cursor into the menu, e.g. when the setting is turned on
    // with the keyboard while the cursor is in the view.
    refresh.current?.();

    return () => {
      props.tool.onMenuTooltipChanged = undefined;
      // And back next to the cursor. Nothing is shown when the tool is turned off, since the
      // cursor is then no longer in the view for it.
      refresh.current?.();
    };
  }, [props.tool]);

  return (
    <Component className={props.className} data-testid="design-menu-tooltip" hidden={props.hidden}>
      {props.title && <Title>{props.title}</Title>}
      {shown.tooltip ? (
        <Properties $stale={shown.stale} data-stale={shown.stale}>
          <AssetPositionProperties
            coordinate={shown.tooltip.coordinate}
            coordinateSystem={shown.tooltip.coordinateSystem}
            heights={shown.tooltip.heights}
            properties={shown.tooltip.properties}
            snapSettings={shown.tooltip.snapSettings}
          />
        </Properties>
      ) : (
        <Placeholder>{t('menuTooltip.placeholder', { ns: 'skyviewDesign' })}</Placeholder>
      )}
    </Component>
  );
};

const Placeholder = styled.span``;

const Properties = styled.div<{ $stale: boolean }>`
  opacity: ${(props) => (props.$stale ? 0.5 : 1)};
  transition: opacity 0.15s;
`;

const Title = styled.div`
  margin-bottom: 0.6em;
  font-size: 0.8em;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.5;
`;

// A flat band, its sides those of what it is in, set apart from what is above it by its line and a
// lighter grey. Smaller, as the text of the dialog.
const Component = styled.div`
  box-sizing: border-box;
  flex-shrink: 0;

  padding: 0.9em 1.5em 1.1em;
  border-top: 1px solid ${(props) => props.theme.color.gray.light};
  background-color: #f7f8f8;
  color: ${(props) => props.theme.color.gray.darkest};

  font-family: 'Montserrat', sans-serif;
  font-size: 9.5pt;

  // The content of the tooltip is made for the dark background next to the cursor.
  && label,
  && ${Placeholder} {
    color: ${(props) => props.theme.color.gray.dark};
  }

  && input {
    background-color: ${(props) => props.theme.color.gray.lightest};
  }

  // Next to the cursor the tooltip is as wide as it needs, here it fits the menu or the dialog: the
  // columns share the width, the labels wrap and nothing has a minimum width. Values don't wrap.
  && * {
    min-width: 0;
  }

  && ${CoordinatePropertiesStyled} {
    grid-auto-columns: minmax(0, 1fr);
    grid-column-gap: 1em;
    grid-row-gap: 0.6em;

    label {
      margin-bottom: 0.1em;
      white-space: normal;
    }
  }

  // The heights keep their width and the names get the ellipsis, the snapped one's mark at the end
  && [data-snapped] > span:first-of-type {
    flex-shrink: 0;
  }

  && [data-snapped='true'] > span:last-child {
    margin-left: auto;
  }
`;
